/**
 * components/Restaurant/OptionGroupModal.js
 * ─────────────────────────────────────────────────────────────────────────────
 * THE option-group editor for restaurant dishes: one modal that CREATES a
 * group or EDITS an existing one (pass `group`).
 *
 * WHY THIS EXISTS (19 Sep 2026). A group could be created from the dish forms
 * but never changed: a typo in "Additional Toppings", a wrong maximum, a
 * missing option all meant delete and recreate, and the create-product form
 * used an older modal that navigated away to /admin/menu on save, throwing
 * the half-typed dish away. EditMenuProductForm carried its own inline copy.
 * This is that copy, lifted out, given an edit mode and a return value.
 *
 * Rules (models/optionModel.js, utilities/optionGroupDraft.js):
 *   minOptions  0 = optional, N = the customer must pick at least N
 *   maxOptions  1 = pick one (radio), N = up to N (checkboxes)
 * `mandatory` on each option is derived server-side from minOptions.
 *
 * Props
 *   show, onClose
 *   ownerId       the restaurant OWNER's user id (options and groups are
 *                 owned by the vendor, not the dish)
 *   productId     attached to a group on create (restaurantProductId)
 *   group         the populated group to edit; null/undefined = create
 *   onSaved(group) called with the server's group after create or update
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "../ui/AppReactSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  createRestaurantOption,
  createRestaurantOptionGroup,
  fetchOptionGroupsRestaurant,
  fetchOptionsRestaurants,
  updateOptionGroup,
} from "../../actions/optionAction.js";
import {
  validateOptionDraft,
  validateOptionGroupDraft,
} from "../../utilities/optionGroupDraft";

const optionLabel = (option) =>
  Number(option?.price) > 0
    ? `${option.name} (+UGX ${Number(option.price).toLocaleString()})`
    : option?.name || "";

/** The picks a populated group carries, as react-select values. */
const picksFromGroup = (group) =>
  (group?.options || [])
    .map((row) => row?.option || row)
    .filter((o) => o && o._id)
    .map((o) => ({ value: o._id, label: optionLabel(o) }));

export default function OptionGroupModal({
  show,
  onClose,
  ownerId,
  productId,
  group = null,
  onSaved,
}) {
  const dispatch = useDispatch();
  const options = useSelector((state) => state.options.options) || [];
  const isEdit = Boolean(group?._id);

  const [name, setName] = useState("");
  const [picks, setPicks] = useState([]);
  const [minOptions, setMinOptions] = useState(0);
  const [maxOptions, setMaxOptions] = useState(1);
  const [newOptionName, setNewOptionName] = useState("");
  const [newOptionPrice, setNewOptionPrice] = useState("");
  const [addingOption, setAddingOption] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Seed from the group being edited each time the modal opens; a fresh
  // create starts empty. Opening used to keep the previous group's draft.
  useEffect(() => {
    if (!show) return;
    setName(group?.name || "");
    setPicks(picksFromGroup(group));
    setMinOptions(group ? Number(group.minOptions) || 0 : 0);
    setMaxOptions(group ? Number(group.maxOptions) || 1 : 1);
    setNewOptionName("");
    setNewOptionPrice("");
    setError("");
    if (ownerId) dispatch(fetchOptionsRestaurants(ownerId));
  }, [show, group, ownerId, dispatch]);

  const handleAddOption = async () => {
    const trimmed = newOptionName.trim();
    const problem = validateOptionDraft({
      name: trimmed,
      price: newOptionPrice,
      existingNames: options.map((o) => o.name),
    });
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setAddingOption(true);
    const created = await dispatch(
      createRestaurantOption({ name: trimmed, price: Number(newOptionPrice || 0), owner: ownerId })
    );
    setAddingOption(false);
    if (!created?._id) return; // the action already reported why
    setPicks((prev) => [...prev, { value: created._id, label: optionLabel(created) }]);
    setNewOptionName("");
    setNewOptionPrice("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    const problem = validateOptionGroupDraft({
      name: trimmed,
      selectedCount: picks.length,
      minOptions,
      maxOptions,
    });
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSaving(true);
    const body = {
      name: trimmed,
      options: picks.map((p) => ({ value: p.value })),
      minOptions: Number(minOptions),
      maxOptions: Number(maxOptions),
    };
    const saved = isEdit
      ? await dispatch(updateOptionGroup(group._id, body))
      : await dispatch(
          createRestaurantOptionGroup({ ...body, restaurantProductId: productId, owner: ownerId })
        );
    setSaving(false);
    if (!saved?._id) return; // the action already toasted the failure
    if (ownerId) dispatch(fetchOptionGroupsRestaurant(ownerId));
    onSaved?.(saved);
    onClose?.();
  };

  const max = Number(maxOptions);
  const ruleHint =
    max === 1
      ? "Customers pick exactly one (shown as radio buttons)."
      : max > 1
        ? `Customers can tick up to ${max} (shown as checkboxes).`
        : "";

  return (
    <Modal show={show} onHide={onClose} className="modal-adorned">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? `Edit ${group?.name || "option group"}` : "Create New Option Group"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Option group name</Form.Label>
            <Form.Control
              type="text"
              placeholder="For example: Offals or Beef"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Form.Text className="text-muted">
              What the customer is choosing between. This is the heading they
              see above the choices.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Options in this group</Form.Label>
            <Select
              isMulti
              options={options.map((o) => ({ value: o._id, label: optionLabel(o) }))}
              value={picks}
              onChange={(next) => setPicks(next || [])}
              placeholder={
                options.length
                  ? "Select options..."
                  : "No options yet for this kitchen. Create one below."
              }
              noOptionsMessage={() => "No options yet for this kitchen. Create one below."}
            />
          </Form.Group>

          <div className="p-3 mb-3 rounded" style={{ background: "#F7F9FB", border: "1px solid #E6EBF1" }}>
            <div className="text-muted mb-2" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>
              Create a new option
            </div>
            <Row className="g-2 align-items-end">
              <Col sm={6}>
                <Form.Label className="mb-1" style={{ fontSize: 12 }}>Name</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="For example: Offals"
                  value={newOptionName}
                  onChange={(e) => setNewOptionName(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter here must add the option, not submit the group.
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
              </Col>
              <Col sm={3}>
                <Form.Label className="mb-1" style={{ fontSize: 12 }}>Extra charge</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  min="0"
                  placeholder="0"
                  value={newOptionPrice}
                  onChange={(e) => setNewOptionPrice(e.target.value)}
                />
              </Col>
              <Col sm={3}>
                <Button
                  type="button"
                  className="btn-adorned-secondary w-100"
                  size="sm"
                  onClick={handleAddOption}
                  disabled={addingOption}
                >
                  {addingOption ? "Adding..." : "Add option"}
                </Button>
              </Col>
            </Row>
            <Form.Text className="text-muted">
              What this choice adds to the dish price. Leave it at 0 for a
              straight swap. Options are shared across this kitchen, so one
              made here can be reused in its other groups.
            </Form.Text>
          </div>

          <Row className="g-3 mb-2">
            <Col sm={6}>
              <Form.Label>Minimum choices</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={minOptions}
                onChange={(e) => setMinOptions(e.target.value)}
              />
              <Form.Text className="text-muted">
                0 makes the group optional. 1 or more makes it required
                before the dish can be added to a cart.
              </Form.Text>
            </Col>
            <Col sm={6}>
              <Form.Label>Maximum choices</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={maxOptions}
                onChange={(e) => setMaxOptions(e.target.value)}
              />
              <Form.Text className="text-muted">
                {ruleHint || "1 for a single choice, more to let customers combine choices such as toppings."}
              </Form.Text>
            </Col>
          </Row>

          {error ? (
            <Alert variant="danger" className="py-2 mb-3">
              {error}
            </Alert>
          ) : null}

          <div className="d-flex justify-content-end">
            <Button type="button" className="btn-adorned-secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button className="btn-adorned-primary ms-2" type="submit" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
